import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { User, Package, FileText, Search as SearchIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GlobalSearchDialog({ open, onOpenChange }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ customers: [], inventory: [], installments: [] });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!q || q.length < 2) { setResults({ customers: [], inventory: [], installments: [] }); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/api/search?q=${encodeURIComponent(q)}`, { withCredentials: true });
        setResults(data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  const go = (path) => {
    onOpenChange(false);
    setQ('');
    navigate(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} shouldFilter={false}>
      <CommandInput
        placeholder="Search by name, CNIC, mobile, IMEI, account #, agreement #..."
        value={q}
        onValueChange={setQ}
        data-testid="global-search-input"
      />
      <CommandList>
        <CommandEmpty>{loading ? 'Searching...' : q.length < 2 ? 'Type at least 2 characters' : 'No results found'}</CommandEmpty>

        {results.customers?.length > 0 && (
          <CommandGroup heading="Customers">
            {results.customers.map((c) => (
              <CommandItem key={c.id} onSelect={() => go('/customers')} data-testid={`search-customer-${c.id}`}>
                <User className="h-4 w-4 mr-2 text-blue-500" />
                <div className="flex flex-col">
                  <span>{c.name}</span>
                  <span className="text-xs text-slate-400">{c.cnic} · {c.mobile} · {c.account_number}</span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.inventory?.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Inventory">
              {results.inventory.map((i) => (
                <CommandItem key={i.id} onSelect={() => go('/inventory')} data-testid={`search-inventory-${i.id}`}>
                  <Package className="h-4 w-4 mr-2 text-purple-500" />
                  <div className="flex flex-col">
                    <span>{i.brand} {i.model}</span>
                    <span className="text-xs text-slate-400">{i.product_type} · {i.imei || i.serial_number || i.vin || '—'}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {results.installments?.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Agreements">
              {results.installments.map((ins) => (
                <CommandItem key={ins.id} onSelect={() => go('/installments')} data-testid={`search-installment-${ins.id}`}>
                  <FileText className="h-4 w-4 mr-2 text-amber-500" />
                  <div className="flex flex-col">
                    <span>{ins.agreement_number}</span>
                    <span className="text-xs text-slate-400">{ins.customer_name} · {ins.account_number}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
